import qs from "query-string";
import { restApiUrl, basicAuth } from "./config";

const log = false; // Set to `false` to disable logs

const apiService = {
  async getEvents(fromDate = null, toDate = null) {
    try {
      // Construct the query string with optional date parameters
      const queryParams = new URLSearchParams();
      if (fromDate) queryParams.append("from", fromDate);
      if (toDate) queryParams.append("to", toDate);

      // Define the endpoint with the query parameters
      const endpoint = `${restApiUrl}/eventtrack?action=read&${queryParams.toString()}`;

      if (log) {
        console.log("Calling getEvents with endpoint: " + endpoint);
      }

      // Send a GET request to the endpoint
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth, // Include authorization if required
          "Content-Type": "application/json", // Set the content type to JSON
        },
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Parse the response as JSON
      const data = await response.json();
      if (log) console.log("getEvents response received:", data);

      // Return the events data
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching event tracking data");
    }
  },
  // Fetch Transaction Summary by Years (multi-select)
  async fetchTransactionSummaryByYears(years = [], country) {
    try {
      // Ensure we handle both arrays and single values
      const yearsParam = Array.isArray(years)
        ? years.map((y) => (typeof y === "object" ? y.year : y)).join(",")
        : String(years);

      // Add country to query string if provided
      const countryParam = country
        ? `&country=${encodeURIComponent(country)}`
        : "";

      const endpoint = `${restApiUrl}/transactions-summary?years=${encodeURIComponent(
        yearsParam,
      )}${countryParam}`;

      if (log) {
        console.log(
          "Calling fetchTransactionSummaryByYears with endpoint:",
          endpoint,
        );
      }

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("fetchTransactionSummaryByYears response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching transaction summary by years");
      throw error;
    }
  },

  async postEventTracking(eventData) {
    try {
      // Define the endpoint for posting event tracking data
      const endpoint = `${restApiUrl}/eventtrack?action=create`;

      if (log) {
        console.log("Calling eventtrack with endpoint: " + endpoint);
        console.log("Event data:", eventData);
      }

      // Send a POST request to the endpoint with event data
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth, // Include authorization if required
          "Content-Type": "application/json", // Set the content type to JSON
        },
        body: JSON.stringify(eventData), // Convert the event data to JSON
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Parse the response as JSON
      const data = await response.json();
      if (log) console.log("postEventTracking response received:", data);

      // Return a success message or data from the response
      return data;
    } catch (error) {
      handleApiError(error, "Error posting event tracking data");
    }
  },
  async fetchTotalPages() {
    try {
      // Define the endpoint for fetching the total number of pages
      const endpoint = `${restApiUrl}/pdf?action=getTotalPages`;

      if (log)
        console.log("Calling fetchTotalPages with endpoint: " + endpoint);

      // Send a GET request to the endpoint
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth, // Include authorization if required
          "Content-Type": "application/json", // Set the content type to JSON for this request
        },
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Parse the response as JSON
      const data = await response.json();
      if (log) console.log("fetchTotalPages response received:", data);

      // Return the total number of pages
      return data.totalPages;
    } catch (error) {
      handleApiError(error, "Error fetching total number of pages");
    }
  },
  async fetchPdfPage(pageNumber) {
    try {
      // Define the endpoint for fetching the PDF page
      const endpoint = `${restApiUrl}/pdf?page=${pageNumber}`;

      if (log) console.log("Calling fetchPdfPage with endpoint: " + endpoint);

      // Send a GET request to the endpoint
      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth, // Include authorization if required
          "Content-Type": "application/pdf", // Set the content type to PDF
        },
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Return the response as a Blob (for binary data like PDFs)
      const pdfBlob = await response.blob();
      if (log)
        console.log("fetchPdfPage response received for page:", pageNumber);

      // Create a URL for the Blob and return it
      const pdfUrl = URL.createObjectURL(pdfBlob);
      return pdfUrl;
    } catch (error) {
      handleApiError(error, "Error fetching PDF page");
    }
  },
  //subscribers
  async createSubscriber(email) {
    try {
      // Define the endpoint for creating subscribers
      const endpoint = `${restApiUrl}/subscribers`;

      if (log)
        console.log("Calling createSubscriber with endpoint: " + endpoint);

      // Send a POST request to the endpoint
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth, // Include authorization if required
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ Email: email }), // Convert the subscriber data to JSON
      });

      // Check if the response is successful
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Parse and return the response data as JSON
      const data = await response.json();
      if (log) console.log("createSubscriber response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error creating subscriber");
    }
  },
  // Create a new PersonWeight record
  async createPersonWeight({
    person_name,
    weight,
    measurement_unit,
    year,
    month,
  }) {
    try {
      const endpoint = `${restApiUrl}/person-weights`;

      if (log)
        console.log("Calling createPersonWeight with endpoint: " + endpoint);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth, // Include auth if needed
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          person_name,
          weight,
          measurement_unit,
          year,
          month,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("createPersonWeight response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error creating PersonWeight");
    }
  },

  // Get all PersonWeight records
  async getPersonWeights() {
    try {
      const endpoint = `${restApiUrl}/person-weights`;

      if (log)
        console.log("Calling getPersonWeights with endpoint: " + endpoint);

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth, // Include auth if needed
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("getPersonWeights response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching PersonWeights");
    }
  },
  // Delete a PersonWeight record by ID
  async deletePersonWeight(id) {
    try {
      const endpoint = `${restApiUrl}/person-weights`;

      if (log)
        console.log("Calling deletePersonWeight with endpoint: " + endpoint);

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }), // send id in request body
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("deletePersonWeight response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error deleting PersonWeight");
    }
  },
  async getDistinctValues(field) {
    try {
      const validFields = {
        category: "categories",
        type: "types",
        bank: "banks",
      };

      if (!validFields[field]) {
        throw new Error(`Invalid distinct field: ${field}`);
      }

      const endpoint = `${restApiUrl}/transactions?distinct=${validFields[field]}`;
      if (log)
        console.log(
          `Calling getDistinctValues(${field}) with endpoint:`,
          endpoint,
        );

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log(`getDistinctValues(${field}) response:`, data);
      return data;
    } catch (error) {
      handleApiError(error, `Error fetching distinct ${field}`);
    }
  },
  async getDistinctCardValues(field) {
    try {
      const validFields = {
        category: "categories",
        type: "types",
        bank: "banks",
      };

      if (!validFields[field]) {
        throw new Error(`Invalid distinct field: ${field}`);
      }

      const endpoint = `${restApiUrl}/expenses?distinct=${validFields[field]}`;
      if (log)
        console.log(
          `Calling getDistinctCardValues(${field}) with endpoint:`,
          endpoint,
        );

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log(`getDistinctValues(${field}) response:`, data);
      return data;
    } catch (error) {
      handleApiError(error, `Error fetching distinct ${field}`);
    }
  },
  async getTransactions({
    pageIndex = 1,
    pageSize = 10,
    fromDate = null,
    toDate = null,
    bankName = null,
    country = null,
    category = null,
    type = null,
  } = {}) {
    try {
      const params = new URLSearchParams();
      params.append("pageIndex", pageIndex);
      params.append("pageSize", pageSize);
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (bankName) params.append("bankName", bankName);
      if (country) params.append("country", country);
      if (category) params.append("category", category);
      if (type) params.append("type", type);

      const endpoint = `${restApiUrl}/transactions?${params.toString()}`;
      if (log) console.log("Calling getTransactions with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("getTransactions response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching transactions");
    }
  },
  async createMonthlyCorp({
    year,
    month,
    india_stock,
    india_bond,
    usa_stock,
    usa_fixed,
    india_pension,
    hsa,
    icici_prulife,
    k_401k,
  }) {
    try {
      const endpoint = `${restApiUrl}/monthly-corps`;

      if (log)
        console.log("Calling createMonthlyCorp with endpoint: " + endpoint);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth, // add auth if needed
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          year,
          month,
          india_stock,
          india_bond,
          usa_stock,
          usa_fixed,
          india_pension,
          hsa,
          icici_prulife,
          k_401k,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("createMonthlyCorp response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error creating MonthlyCorp");
    }
  },
  async createTransaction({
    country,
    transaction_date,
    bank_name,
    transaction_type,
    amount,
    category,
    description,
  }) {
    try {
      const endpoint = `${restApiUrl}/transactions`;

      if (log)
        console.log("Calling createTransaction with endpoint: " + endpoint);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth, // add auth if needed
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          country,
          transaction_date,
          bank_name,
          transaction_type,
          amount,
          category,
          description,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("createTransaction response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error creating Transaction");
    }
  },

  async getMonthlyCorps() {
    try {
      const endpoint = `${restApiUrl}/monthly-corps`;

      if (log)
        console.log("Calling getMonthlyCorps with endpoint: " + endpoint);

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth, // add auth if needed
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("getMonthlyCorps response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching MonthlyCorps");
    }
  },

  // Delete a MonthlyCorps record by ID
  async deleteMonthlyCorp(id) {
    try {
      const endpoint = `${restApiUrl}/monthly-corps`;

      if (log)
        console.log("Calling deleteMonthlyCorp with endpoint: " + endpoint);

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          Authorization: basicAuth, // add auth if needed
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("deleteMonthlyCorp response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error deleting MonthlyCorp");
    }
  },
  async updateMonthlyCorp(id, data) {
    if (!id) throw new Error("ID is required for update");

    const endpoint = `${restApiUrl}/monthly-corps`;

    // Include id in data object
    const payload = { id, ...data };

    const response = await fetch(endpoint, {
      method: "PUT",
      headers: {
        Authorization: basicAuth, // add if needed
        "Content-Type": "application/json", // Send JSON
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `HTTP error! Status: ${response.status}, Response: ${errorText}`,
      );
    }

    const result = await response.json();
    return result;
  },

  //users
  async getUsers(page = 1, itemsPerPage = 10, role = "", search = "") {
    try {
      // Construct the endpoint URL with query parameters for pagination, search, and filter
      const endpoint = `${restApiUrl}/users?page=${page}&items_per_page=${itemsPerPage}&role=${encodeURIComponent(
        role,
      )}&emailOrName=${encodeURIComponent(search)}`;

      if (log) console.log("Calling getUsers with endpoint: " + endpoint);

      // Fetch data from the API
      const response = await fetch(endpoint, {
        timeout: 60000, // 60 seconds
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      // Check if the response status is OK
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Parse JSON data from the response
      const data = await response.json();
      if (log) console.log("getUsers response:", data);

      // Return the users data
      return {
        records: data.records,
        totalPages: data.total_pages,
        currentPage: data.current_page,
      };
    } catch (error) {
      handleApiError(error, "Error fetching users");
      // Ensure to return a consistent structure even in error cases if needed
      return {
        records: [],
        totalPages: 0,
        currentPage: page,
      };
    }
  },
  async updateUser(data) {
    try {
      // Define the endpoint URL
      const endpoint = `${restApiUrl}/users`;

      // Log the data if needed
      if (log) console.log("Calling updateUser with data:", data);

      // Make the PUT request to the API endpoint
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "PUT",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      // Check if the response is OK
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // Parse the response JSON
      const responseData = await response.json();

      // Log the response data if needed
      if (log) console.log("updateUser response:", responseData);

      // Return the response data
      return responseData;
    } catch (error) {
      // Handle any errors that occur during the fetch
      handleApiError(error, "Error updating user");
    }
  },
  async deleteUser(userId) {
    try {
      const endpoint = `${restApiUrl}/users?UserID=${userId}`;
      if (log) console.log("Calling deleteUser with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("deleteUser response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error deleting user");
    }
  },

  //Reaction

  async createBlogReaction(blogPostData) {
    try {
      const endpoint = `${restApiUrl}/blogReactions`;
      if (log)
        console.log("Calling createBlogReaction with endpoint: " + endpoint);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(blogPostData),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("createBlogReaction response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error creating blog post");
    }
  },

  async deleteBlogReaction(reactionId) {
    try {
      const endpoint = `${restApiUrl}/blogReactions?id=${reactionId}`;
      if (log)
        console.log("Calling deleteBlogReaction with endpoint: " + endpoint);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "DELETE",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("deleteBlogReaction response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error deleting blog post");
    }
  },
  async getBlogReactionByPostId(postId) {
    try {
      const endpoint = `${restApiUrl}/blogReactions?postid=${postId}`;
      if (log)
        console.log(
          "Calling getBlogReactionByPostId with endpoint: " + endpoint,
        );
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("getBlogReactionByPostId response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching blog post by ID");
    }
  },

  async updateBlogReaction(reactionId, data) {
    try {
      const endpoint = `${restApiUrl}/blogReactions?id=${reactionId}`;
      if (log) console.log("Calling updateBlogReaction with data:", data);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "PUT",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("updateBlogReaction response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error updating blog reaction");
    }
  },
  //blogpost
  async getBlogPost(category, seoUrl) {
    try {
      const endpoint = `${restApiUrl}/blogPosts?category=${category}&seourl=${seoUrl}`;
      if (log) console.log("Calling getBlogPost with endpoint: " + endpoint);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("getBlogPost response:", data);
      return data.records[0];
    } catch (error) {
      handleApiError(error, "Error fetching blog post");
    }
  },

  // Fallback: fetch blog post by SEOUrl only (when category path doesn't match backend category)
  async getBlogPostBySeoUrl(seoUrl) {
    try {
      const endpoint = `${restApiUrl}/blogPosts?seourl=${seoUrl}`;
      if (log)
        console.log("Calling getBlogPostBySeoUrl with endpoint: " + endpoint);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("getBlogPostBySeoUrl response:", data);
      return data.records && data.records.length ? data.records[0] : null;
    } catch (error) {
      handleApiError(error, "Error fetching blog post by seourl");
    }
  },

  async getBlogReactionById(postId) {
    try {
      const endpoint = `${restApiUrl}/blogReactions?postid=${postId}`;
      if (log)
        console.log("Calling getBlogPostById with endpoint: " + endpoint);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("getBlogPostById response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching blog post by ID");
    }
  },

  async getBlogPostByPostID(PostID) {
    try {
      const endpoint = `${restApiUrl}/blogPosts?postid=${PostID}`;
      if (log)
        console.log("Calling getBlogPostByPostID with endpoint: " + endpoint);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("getBlogPostByPostID response:", data);
      return data.records[0];
    } catch (error) {
      handleApiError(error, "Error fetching blog post");
    }
  },

  async createBlogPost(data) {
    try {
      const endpoint = `${restApiUrl}/blogPosts`;
      if (log) console.log("Calling createBlogPost with data:", data);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("createBlogPost response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error creating blog post");
    }
  },

  async approveRejectArticle(PostID, Status) {
    try {
      const endpoint = `${restApiUrl}/approvereject?postid=${PostID}&status=${Status}`;
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("getBlogPostByPostID response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error updating blog post");
    }
  },

  async updateBlogPost(data, PostID, Published) {
    try {
      const endpoint = `${restApiUrl}/blogPosts?postid=${PostID}`;
      if (log) console.log("Calling updateBlogPost with data:", data);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "PUT",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("updateBlogPost response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error updating blog post");
    }
  },

  async deleteComment(commentId) {
    const payload = {
      CommentID: commentId,
    };
    const endpoint = `${restApiUrl}/comments`;
    if (log) console.log("Calling Delete comments with endpoint: " + endpoint);

    const response = await fetch(endpoint, {
      method: "DELETE",
      headers: {
        Authorization: basicAuth,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return response.json();
  },

  async postComment(postId, userId, content) {
    const payload = {
      PostID: postId,
      UserID: userId,
      Content: content,
    };
    const endpoint = `${restApiUrl}/comments`;
    if (log) console.log("Calling posting comments with endpoint: " + endpoint);

    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        Authorization: basicAuth,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async updateComment(commentId, userId, content) {
    const payload = {
      CommentID: commentId,
      UserID: userId,
      Content: content,
    };
    const endpoint = `${restApiUrl}/comments`;
    if (log) console.log("Calling posting comments with endpoint: " + endpoint);

    const response = await fetch(endpoint, {
      method: "PUT",
      headers: {
        Authorization: basicAuth,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  async deleteBlogPost(PostID, Status) {
    try {
      const endpoint = `${restApiUrl}/blogPosts?postid=${PostID}&status=${Status}`;
      if (log) console.log("Calling deleteBlogPost with endpoint: " + endpoint);
      const response = await fetch(endpoint, {
        method: "DELETE",
        timeout: 60000,
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("deleteBlogPost response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error deleting blog post");
    }
  },

  async uploadImage(formData) {
    try {
      const endpoint = `${restApiUrl}/uploadImage`;
      if (log) console.log("Calling uploadImage with formData:", formData);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "POST",
        headers: {
          Authorization: basicAuth,
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("uploadImage response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error uploading image");
      throw error;
    }
  },

  async removeImage(filename, directory = "") {
    try {
      let endpoint = `${restApiUrl}/uploadImage?filename=${filename}`;
      if (directory) {
        endpoint += `&uploadDir=${encodeURIComponent(directory)}`;
      }
      console.log(endpoint);
      if (log) console.log("Calling removeImage with filename: " + filename);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "DELETE",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("removeImage response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error removing image");
    }
  },

  async fetchBlog() {
    const endpoint = `${restApiUrl}/blogs`;
    if (log) console.log("Calling fetchBlog with endpoint: " + endpoint);
    try {
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("fetchBlog response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching blog posts");
      throw error;
    }
  },

  async fetchUrl() {
    const endpoint = `${restApiUrl}/url`;
    if (log) console.log("Calling fetchUrl with endpoint: " + endpoint);
    try {
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("fetchUrl response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching URL");
      throw error;
    }
  },

  async fetchBlogPostByCategory(
    category,
    currentpage = 1,
    pageSize = 8,
    searchTitle = "",
    status = 1,
  ) {
    try {
      const endpoint = `${restApiUrl}/blogPosts?category=${category}&pageIndex=${currentpage}&pageSize=${pageSize}&searchTitle=${searchTitle}&status=${status}`;
      if (log)
        console.log(
          "Calling fetchBlogPostByCategory with endpoint: " + endpoint,
        );
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("fetchBlogPostByCategory response:", data);
      return data;
    } catch (error) {
      handleApiError(
        error,
        `Error fetching blog posts for category ${category}`,
      );
      throw error;
    }
  },
  async socialLogin(action, Token) {
    try {
      const formData = qs.stringify({
        action: action,
        Token: Token,
      });
      const endpoint = `${restApiUrl}/login`;
      if (log) console.log("Calling socialLogin with endpoint:", endpoint);
      if (log) console.log("socialLogin formData:", formData);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });

      if (log) console.log("socialLogin response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! Status: ${response.status}, Response: ${errorText}`,
        );
      }

      const responseData = await response.json();
      if (log) console.log("socialLogin response data:", responseData);
      return responseData;
    } catch (error) {
      console.error("socialLogin error:", error);
      handleApiError(error, "Error logging in with social provider");
      throw error;
    }
  },
  async login(email, password, action) {
    try {
      const formData = qs.stringify({
        action: action,
        useremail: email,
        password: password,
      });
      const endpoint = `${restApiUrl}/login`;
      if (log) console.log("Calling login with formData:", formData);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("login response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error logging in");
    }
  },

  async logout(sessionId) {
    try {
      const formData = qs.stringify({
        sessionId: sessionId,
      });
      const endpoint = `${restApiUrl}/logout`;
      if (log) console.log("Calling logout with formData:", formData);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("logout response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error logout in");
    }
  },
  async fetchTableData(endpoint, payload) {
    // endpoint: "/api/generate-doc" or "/api/list-tables"
    // payload: JSON object containing credentials and parameters
    const url = `${restApiUrl}${endpoint}`;

    const headers = {
      "Content-Type": "application/json",
      Authorization: basicAuth, // Replace with your actual Basic Auth token
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error(`Fetch error for ${endpoint}:`, error);
      return { error: error.message };
    }
  },
  async fetchSEOData($url) {
    const endpoint = `${restApiUrl}/python`;
    const data = { url: $url };
    const headers = {
      "Content-Type": "application/json",
      Authorization: basicAuth, // Replace with your actual authorization token
    };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
      // Handle the response data as needed
    } catch (error) {
      console.error("Fetch error:", error);
    }
  },
  async fetchSummaryBlogs($url) {
    const endpoint = `${restApiUrl}/extract-blog`;
    const data = { url: $url };
    const headers = {
      "Content-Type": "application/json",
      Authorization: basicAuth, // Replace with your actual authorization token
    };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
      // Handle the response data as needed
    } catch (error) {
      console.error("Fetch error:", error);
    }
  },
  async fetchInfoGraphic($text) {
    const endpoint = `${restApiUrl}/infographic`;
    const data = { text: $text };
    const headers = {
      "Content-Type": "application/json",
      Authorization: basicAuth, // Replace with your actual authorization token
    };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
      // Handle the response data as needed
    } catch (error) {
      console.error("Fetch error:", error);
    }
  },
  async fetchflashcard($text) {
    const endpoint = `${restApiUrl}/flashcard`;
    const data = { text: $text };
    const headers = {
      "Content-Type": "application/json",
      Authorization: basicAuth, // Replace with your actual authorization token
    };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
      // Handle the response data as needed
    } catch (error) {
      console.error("Fetch error:", error);
    }
  },
  async fetchmindmap($text) {
    const endpoint = `${restApiUrl}/mindmap`;
    const data = { text: $text };
    const headers = {
      "Content-Type": "application/json",
      Authorization: basicAuth, // Replace with your actual authorization token
    };

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: headers,
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
      // Handle the response data as needed
    } catch (error) {
      console.error("Fetch error:", error);
    }
  },
  async checkSession(sessionId) {
    try {
      const formData = qs.stringify({ sessionId: sessionId, action: "login" });
      const endpoint = `${restApiUrl}/login`;
      if (log) console.log("Calling checkSession with formData:", formData);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("checkSession response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error checking session");
    }
  },

  async sendEmail(data) {
    try {
      const formData = qs.stringify(data);
      const endpoint = `${restApiUrl}/sendEmail`;
      if (log) console.log("Calling sendEmail with data:", data);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formData,
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("sendEmail response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error sending email");
    }
  },

  // Transaction API
  async fetchTransactions({
    pageIndex = 1,
    pageSize = 10,
    fromDate = null,
    toDate = null,
    bankName = null,
    country = null,
    category = null,
    type = null,
  } = {}) {
    try {
      const params = new URLSearchParams();
      params.append("pageIndex", pageIndex);
      params.append("pageSize", pageSize);
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (bankName) params.append("bankName", bankName);
      if (country) params.append("country", country);
      if (category) params.append("category", category);
      if (type) params.append("type", type);
      const endpoint = `${restApiUrl}/transactions?${params.toString()}`;
      if (log)
        console.log("Calling fetchTransactions with endpoint:", endpoint);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("fetchTransactions response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching transactions");
      throw error;
    }
  },

  // Update Transaction API
  async updateTransaction(transactionId, data) {
    try {
      const endpoint = `${restApiUrl}/transactions?id=${transactionId}`;
      // Only send id and category fields, as required by backend
      const payload = {
        id: Number(data.id),
        category: String(data.category),
      };
      if (log)
        console.log("Calling updateTransaction with payload (form):", payload);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "PUT",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("updateTransaction response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error updating transaction");
      throw error;
    }
  },

  // Transaction Monthly Summary API
  async fetchTransactionMonthlySummary({
    fromDate = null,
    toDate = null,
    bankName = null,
    type = null,
  } = {}) {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (bankName) params.append("bankName", bankName);
      if (type) params.append("type", type);
      const endpoint = `${restApiUrl}/transactions-monthly-summary?${params.toString()}`;
      if (log)
        console.log(
          "Calling fetchTransactionMonthlySummary with endpoint:",
          endpoint,
        );
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("fetchTransactionMonthlySummary response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching transaction monthly summary");
      throw error;
    }
  },

  // Fetch Expenses API
  // Update Expense API
  async updateExpense(expenseId, data) {
    try {
      const endpoint = `${restApiUrl}/expenses?id=${expenseId}`;
      // Only send id and category fields, as required by backend
      const payload = {
        id: Number(data.id),
        category: String(data.category),
      };
      if (log)
        console.log("Calling updateExpense with payload (form):", payload);
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "PUT",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const responseData = await response.json();
      if (log) console.log("updateExpense response:", responseData);
      return responseData;
    } catch (error) {
      handleApiError(error, "Error updating expense");
      throw error;
    }
  },
  async fetchExpenses({
    pageIndex = 1,
    pageSize = 10,
    fromDate = null,
    toDate = null,
    bankName = null,
    category = null,
    type = null,
  } = {}) {
    try {
      const params = new URLSearchParams();
      params.append("pageIndex", pageIndex);
      params.append("pageSize", pageSize);
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (bankName) params.append("bankName", bankName);
      if (category) params.append("category", category);
      if (type) params.append("type", type);

      const endpoint = `${restApiUrl}/expenses?${params.toString()}`;
      if (log) console.log("Calling fetchExpenses with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("fetchExpenses response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching expenses");
      throw error;
    }
  },

  // Expense Monthly Summary API
  async fetchExpenseMonthlySummary({
    fromDate = null,
    toDate = null,
    bankName = null,
    category = null,
    type = null,
  } = {}) {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (bankName) params.append("bankName", bankName);
      if (category) params.append("category", category);
      if (type) params.append("type", type);
      const endpoint = `${restApiUrl}/expenses-monthly-summary?${params.toString()}`;
      if (log)
        console.log(
          "Calling fetchExpenseMonthlySummary with endpoint:",
          endpoint,
        );
      const response = await fetch(endpoint, {
        timeout: 60000,
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      if (log) console.log("fetchExpenseMonthlySummary response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching expense monthly summary");
      throw error;
    }
  },

  // Goals Management API Methods
  async getGoals(filters = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.category) queryParams.append("category", filters.category);
      if (filters.id) queryParams.append("id", filters.id);
      if (filters.action) queryParams.append("action", filters.action);

      const endpoint = `${restApiUrl}/my-goals${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      if (log) console.log("Calling getGoals with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("getGoals response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching goals");
      throw error;
    }
  },

  async createGoal(goalData) {
    try {
      const endpoint = `${restApiUrl}/my-goals`;

      if (log) console.log("Calling createGoal with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(goalData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("createGoal response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error creating goal");
      throw error;
    }
  },

  async updateGoal(id, goalData) {
    try {
      const endpoint = `${restApiUrl}/my-goals`;

      // Include id in the payload
      const payload = { id, ...goalData };

      if (log) console.log("Calling updateGoal with endpoint:", endpoint);
      if (log) console.log("updateGoal payload:", payload);

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("updateGoal response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error updating goal");
      throw error;
    }
  },

  async deleteGoal(id) {
    try {
      const endpoint = `${restApiUrl}/my-goals`;

      if (log) console.log("Calling deleteGoal with endpoint:", endpoint);
      if (log) console.log("deleteGoal payload:", { id });

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("deleteGoal response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error deleting goal");
      throw error;
    }
  },

  // Bill Management API Methods
  async uploadBill(formData) {
    try {
      const endpoint = `${restApiUrl}/bills`;
      if (log) console.log("Calling uploadBill with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("uploadBill response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error uploading bill");
      throw error;
    }
  },

  async getBills({
    page = 1,
    per_page = 10,
    user_id = null,
    search = null,
    category = null,
    merchant_name = null,
    payment_method = null,
    date_from = null,
    date_to = null,
    min_amount = null,
    max_amount = null,
    sort_by = null,
    sort_order = null,
  } = {}) {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("per_page", per_page);
      if (user_id) params.append("user_id", user_id);
      if (search) params.append("search", search);
      if (category) params.append("category", category);
      if (merchant_name) params.append("merchant_name", merchant_name);
      if (payment_method) params.append("payment_method", payment_method);
      if (date_from) params.append("date_from", date_from);
      if (date_to) params.append("date_to", date_to);
      if (min_amount) params.append("min_amount", min_amount);
      if (max_amount) params.append("max_amount", max_amount);
      if (sort_by) params.append("sort_by", sort_by);
      if (sort_order) params.append("sort_order", sort_order);

      const endpoint = `${restApiUrl}/bills?${params.toString()}`;
      if (log) console.log("Calling getBills with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("getBills response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching bills");
      throw error;
    }
  },

  async getBillById(billId) {
    try {
      const endpoint = `${restApiUrl}/bills?billid=${billId}`;
      if (log) console.log("Calling getBillById with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("getBillById response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error fetching bill details");
      throw error;
    }
  },

  async updateBill(billId, billData) {
    try {
      const endpoint = `${restApiUrl}/bills?billid=${billId}`;
      if (log) console.log("Calling updateBill with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(billData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("updateBill response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error updating bill");
      throw error;
    }
  },

  async deleteBill(billId) {
    try {
      const endpoint = `${restApiUrl}/bills?billid=${billId}`;
      if (log) console.log("Calling deleteBill with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("deleteBill response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error deleting bill");
      throw error;
    }
  },

  // Bill Item Management API Methods
  async addBillItem(billId, itemData) {
    try {
      const endpoint = `${restApiUrl}/bills/items?billid=${billId}`;
      if (log) console.log("Calling addBillItem with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(itemData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("addBillItem response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error adding bill item");
      throw error;
    }
  },

  async updateBillItem(billId, itemId, itemData) {
    try {
      const endpoint = `${restApiUrl}/bills/items?billid=${billId}&itemid=${itemId}`;
      if (log) console.log("Calling updateBillItem with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(itemData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("updateBillItem response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error updating bill item");
      throw error;
    }
  },

  async deleteBillItem(billId, itemId) {
    try {
      const endpoint = `${restApiUrl}/bills/items?billid=${billId}&itemid=${itemId}`;
      if (log) console.log("Calling deleteBillItem with endpoint:", endpoint);

      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("deleteBillItem response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error deleting bill item");
      throw error;
    }
  },

  // ============================================
  // STUDY HUB API METHODS
  // ============================================

  // SUBJECT MANAGEMENT
  async createSubject(userId, data) {
    try {
      const response = await fetch(`${restApiUrl}/subjects`, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: userId, ...data }),
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error creating subject");
      throw error;
    }
  },

  async getSubjects(userId, params = {}) {
    try {
      const queryString = qs.stringify({ user_id: userId, ...params });
      const response = await fetch(`${restApiUrl}/subjects?${queryString}`, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching subjects");
      throw error;
    }
  },

  async getSubjectById(userId, subjectId) {
    try {
      const response = await fetch(
        `${restApiUrl}/subjects?user_id=${userId}&subject_id=${subjectId}`,
        {
          method: "GET",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching subject");
      throw error;
    }
  },

  async updateSubject(userId, subjectId, data) {
    try {
      const response = await fetch(
        `${restApiUrl}/subjects?user_id=${userId}&subject_id=${subjectId}`,
        {
          method: "PUT",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error updating subject");
      throw error;
    }
  },

  async deleteSubject(userId, subjectId) {
    try {
      const response = await fetch(
        `${restApiUrl}/subjects?user_id=${userId}&subject_id=${subjectId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error deleting subject");
      throw error;
    }
  },

  // SOURCE MANAGEMENT
  async createSource(userId, data) {
    try {
      const response = await fetch(`${restApiUrl}/sources`, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: userId, ...data }),
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error creating source");
      throw error;
    }
  },

  async getSources(userId, params = {}) {
    try {
      const queryString = qs.stringify({ user_id: userId, ...params });
      const response = await fetch(`${restApiUrl}/sources?${queryString}`, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching sources");
      throw error;
    }
  },

  async getSourceById(userId, sourceId) {
    try {
      const response = await fetch(
        `${restApiUrl}/sources?user_id=${userId}&source_id=${sourceId}`,
        {
          method: "GET",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching source");
      throw error;
    }
  },

  async updateSource(userId, sourceId, data) {
    try {
      const response = await fetch(
        `${restApiUrl}/sources?user_id=${userId}&source_id=${sourceId}`,
        {
          method: "PUT",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error updating source");
      throw error;
    }
  },

  async deleteSource(userId, sourceId) {
    try {
      const response = await fetch(
        `${restApiUrl}/sources?user_id=${userId}&source_id=${sourceId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error deleting source");
      throw error;
    }
  },

  // NOTE MANAGEMENT
  async createNote(userId, data) {
    try {
      const response = await fetch(`${restApiUrl}/notes`, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: userId, ...data }),
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error creating note");
      throw error;
    }
  },

  async getNotes(userId, params = {}) {
    try {
      const queryString = qs.stringify({ user_id: userId, ...params });
      const response = await fetch(`${restApiUrl}/notes?${queryString}`, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching notes");
      throw error;
    }
  },

  async getNoteById(userId, noteId) {
    try {
      const response = await fetch(
        `${restApiUrl}/notes?user_id=${userId}&note_id=${noteId}`,
        {
          method: "GET",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching note");
      throw error;
    }
  },

  async updateNote(userId, noteId, data) {
    try {
      const response = await fetch(
        `${restApiUrl}/notes?user_id=${userId}&note_id=${noteId}`,
        {
          method: "PUT",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error updating note");
      throw error;
    }
  },

  async deleteNote(userId, noteId) {
    try {
      const response = await fetch(
        `${restApiUrl}/notes?user_id=${userId}&note_id=${noteId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error deleting note");
      throw error;
    }
  },

  // STUDY LOG MANAGEMENT
  async createStudyLog(userId, data) {
    try {
      const response = await fetch(`${restApiUrl}/study-log`, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id: userId, ...data }),
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error creating study log");
      throw error;
    }
  },

  async getStudyLogs(userId, params = {}) {
    try {
      const queryString = qs.stringify({ user_id: userId, ...params });
      const response = await fetch(`${restApiUrl}/study-log?${queryString}`, {
        method: "GET",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching study logs");
      throw error;
    }
  },

  async getStudyStatistics(userId, params = {}) {
    try {
      const queryString = qs.stringify({ user_id: userId, ...params });
      const response = await fetch(
        `${restApiUrl}/study-log/statistics?${queryString}`,
        {
          method: "GET",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching study statistics");
      throw error;
    }
  },

  async getStudyLogById(userId, studyLogId) {
    try {
      const response = await fetch(
        `${restApiUrl}/study-log?user_id=${userId}&study_log_id=${studyLogId}`,
        {
          method: "GET",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error fetching study log");
      throw error;
    }
  },

  async updateStudyLog(userId, studyLogId, data) {
    try {
      const response = await fetch(
        `${restApiUrl}/study-log?user_id=${userId}&study_log_id=${studyLogId}`,
        {
          method: "PUT",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error updating study log");
      throw error;
    }
  },

  async deleteStudyLog(userId, studyLogId) {
    try {
      const response = await fetch(
        `${restApiUrl}/study-log?user_id=${userId}&study_log_id=${studyLogId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: basicAuth,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      return await response.json();
    } catch (error) {
      handleApiError(error, "Error deleting study log");
      throw error;
    }
  },

  // ============================================
  // AI CHAT COMPLETION API METHODS
  // ============================================

  /**
   * Get AI completion for a single prompt
   * @param {string} prompt - The user's prompt
   * @param {object} options - Optional parameters (temperature, max_tokens, model)
   * @returns {Promise<object>} API response
   */
  async getChatCompletion(prompt, options = {}) {
    const {
      temperature = 0.7,
      max_tokens = 1000,
      model = "openai/gpt-oss-20b",
    } = options;

    try {
      const endpoint = `${restApiUrl}/chat-completion`;
      if (log)
        console.log(`Calling getChatCompletion with endpoint: ${endpoint}`);

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt,
          temperature,
          max_tokens,
          model,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("Chat completion response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error getting chat completion");
      throw error;
    }
  },

  /**
   * Get AI completion with conversation history
   * @param {array} messages - Array of message objects {role, content}
   * @param {object} options - Optional parameters
   * @returns {Promise<object>} API response
   */
  async getChatCompletionWithHistory(messages, options = {}) {
    const {
      temperature = 0.7,
      max_tokens = 1000,
      model = "openai/gpt-oss-20b",
    } = options;

    try {
      const endpoint = `${restApiUrl}/chat-completion-history`;
      if (log)
        console.log(
          `Calling getChatCompletionWithHistory with endpoint: ${endpoint}`,
        );

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Authorization: basicAuth,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          messages,
          temperature,
          max_tokens,
          model,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      if (log) console.log("Chat completion with history response:", data);
      return data;
    } catch (error) {
      handleApiError(error, "Error getting chat completion with history");
      throw error;
    }
  },

  /**
   * Generate a subject description based on title
   */
  async generateSubjectDescription(subjectName) {
    const prompt = `Write a brief, informative description (2-3 sentences) for a study subject titled "${subjectName}". Focus on what this subject is about and why it's important to learn.`;
    return await this.getChatCompletion(prompt, {
      temperature: 0.7,
      max_tokens: 200,
    });
  },

  /**
   * Generate note content based on title and context
   */
  async generateNoteContent(noteTitle, subjectName, sourceName = null) {
    let prompt = `Write detailed study notes about "${noteTitle}" for the subject "${subjectName}".`;
    if (sourceName) {
      prompt += ` The source is "${sourceName}".`;
    }
    prompt += ` Include key concepts, definitions, and important points.`;

    return await this.getChatCompletion(prompt, {
      temperature: 0.7,
      max_tokens: 800,
    });
  },

  /**
   * Expand/improve existing text
   */
  async improveText(text, context = "") {
    const prompt = `Improve and expand the following text${context ? ` about ${context}` : ""}: "${text}". Make it more detailed, clear, and informative while maintaining the original intent.`;
    return await this.getChatCompletion(prompt, {
      temperature: 0.7,
      max_tokens: 500,
    });
  },

  /**
   * Rewrite selected content based on subject context
   */
  async rewriteSelectedText(selectedText, subjectName = "") {
    const contextText = subjectName ? ` for the subject "${subjectName}"` : "";
    const prompt = `Rewrite the following selected text${contextText}. Preserve the meaning, improve clarity, and keep the tone consistent. Selected text: "${selectedText}"`;
    return await this.getChatCompletion(prompt, {
      temperature: 0.6,
      max_tokens: 400,
    });
  },

  /**
   * Generate source description
   */
  async generateSourceDescription(sourceName, sourceType) {
    const prompt = `Write a brief description (2-3 sentences) for a learning resource titled "${sourceName}" which is a ${sourceType}. Explain what learners might expect from this resource.`;
    return await this.getChatCompletion(prompt, {
      temperature: 0.7,
      max_tokens: 200,
    });
  },
};

function handleApiError(error, defaultMessage) {
  let errorMessage = defaultMessage;
  if (error.response) {
    errorMessage += `: ${
      error.response.data.message || error.response.statusText
    }`;
  } else {
    errorMessage += `: ${error.message}`;
  }
  console.error(errorMessage);
  throw new Error(errorMessage); // Optionally throw the error to propagate it further
}

export default apiService;
